import {
  K3Extensions,
  useTypedSelector,
  useValues,
  useVariables,
  Value,
  VariableT,
} from '@k3/core';
import { DeepRequired } from '../types/DeepRequired';
import { RuleEngineState } from '@k3/core/dist/src/lib/engine/ruleEngine2/types';

export const noSelectionKey = '$noSelection';

export const switchAndSubvariableKeys = [
  {
    switchVariableKey: 'kabelketteSchalter',
    subVariableKey: 'kabelkette',
  },
  {
    switchVariableKey: 'kabeldurchlassFarbe',
    subVariableKey: 'kabeldurchlass',
  },
  {
    switchVariableKey: 'orgaboxSchalter',
    subVariableKey: 'orgabox',
  },
  {
    switchVariableKey: 'orgaboxSchalter',
    subVariableKey: 'orgabox',
  },
  {
    switchVariableKey: 'kasten',
    subVariableKey: 'kastenPosition',
  },
  {
    switchVariableKey: 'kabelnetzEckSchalter',
    subVariableKey: 'kabelnetzEck',
  },
  {
    switchVariableKey: 'kabelwanneEckSchalter',
    subVariableKey: 'kabelwanneEck',
  },
];

const ignoredConflicts = [['kabelnetz', 'kabelwanne']];

const isIgnoredConflict = (
  variables: VariableT[],
  conflict: RuleEngineState | undefined
) => {
  if (!conflict) return false;
  const conflictingId = conflict.lastManualChange?.variableId;
  const changes = conflict.touchedChanges;
  const numberOfChanges = changes.length;

  const allConflictingVariableIds = [
    ...changes.map((c) => c[0].variableId),
    conflictingId,
  ];
  const allConflictingVariableKeys = allConflictingVariableIds.map(
    (variableId) => variables.find((v) => v.id === variableId)?.key
  );

  if (allConflictingVariableKeys.some((k) => !k)) return false;

  const potentialMatches = ignoredConflicts.filter(
    (c) => c.length === numberOfChanges + 1
  );

  return potentialMatches.some((m) =>
    m.every((variableKey) => allConflictingVariableKeys.includes(variableKey))
  );
};

export function isConflictWithJustAnOptionalValue(
  variables: VariableT[],
  values: Value[],
  conflict: RuleEngineState | undefined,
  switchVariableKey: string,
  subVariableKey: string
) {
  const switchVariable = variables?.find((v) => v.key === switchVariableKey);
  const subVariable = variables?.find((v) => v.key === subVariableKey);
  const subvariableOptionalValue = values?.find(
    (v) => v.variableId === subVariable?.id && v.key === noSelectionKey
  );

  const isConflictAboutChangingTopLevelVariable =
    switchVariable &&
    conflict &&
    conflict?.lastManualChange?.variableId === switchVariable.id;

  if (
    subVariable &&
    subvariableOptionalValue &&
    isConflictAboutChangingTopLevelVariable
  ) {
    const isConflictAboutChangingToOrFromOptionalValue =
      conflict.changes.length === 1 &&
      conflict.changes[0].find(
        (selection) =>
          selection.variableId === subVariable?.id &&
          selection.valueId === subvariableOptionalValue.id
      );

    return isConflictAboutChangingToOrFromOptionalValue;
  }

  return false;
}

export const InvalidRuleModal: DeepRequired<K3Extensions>['app']['invalidRuleModal']['root'] =
  (Wrapped) => {
    return (props) => {
      const conflict = useTypedSelector(
        (state) => state.ruleEngine.ruleEngineConflict?.ruleEngineState
      );

      const variables = useVariables();
      const values = useValues();

      if (
        switchAndSubvariableKeys.some(({ switchVariableKey, subVariableKey }) =>
          isConflictWithJustAnOptionalValue(
            variables,
            values,
            conflict,
            switchVariableKey,
            subVariableKey
          )
        ) ||
        isIgnoredConflict(variables, conflict)
      ) {
        props.acceptAlternative();
        return null;
      }

      const modelConflict = props.conflicts.find(
        (c) => c?.current?.variable === 'Modell'
      );

      if (modelConflict) {
        setTimeout(() => {
          props.rejectAlternative();
        }, 0);
        return null;
      }

      return <Wrapped {...props} />;
    };
  };
